import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router';
import { SegmentUiElements } from '@foxcorp/delta-web-analytics';
import { LAST_SESSION_PATH } from 'helpers/RouteHelper';
import { noop } from 'helpers/GlobalHelper';
import { COLLECTION_TYPE_GENRE, COLLECTION_TYPE_LIVE, isInternalLink } from 'helpers/ContentHelper';
import { setSessionStorageItem } from 'helpers/Storage';

import Badge from 'components/Badge';
import {
  Detail,
  Metadata,
  MobilePlayButton,
  Overlay,
  PlayButton,
  Subtitle,
  TileImage,
  TileTitle,
} from '../../../components';
import {
  titleProcessor,
  playButtonClickProcessor,
  isMobileDevice,
} from '../../../helpers/processorHelper';
import Style from '../../../Styles/ClipTile.module.scss';

const ClipTile = props => {
  const {
    account,
    altText,
    badge,
    breakpoint,
    collectionType,
    durationInSeconds,
    gridIndex,
    gridTitle,
    hoverImage,
    image,
    isRedirect,
    isSeries,
    itemIndex,
    link,
    name,
    network,
    metadata,
    onTileClick,
    onTitleClick,
    originalItem,
    percentWatched,
    recommendationIdData,
    scaleFromLeft,
    scaleFromRight,
    subtitle,
    subtitleLines,
    subtitleText,
    target,
    theme,
    title,
    titleLink,
    titleText,
    truncateSubtitle,
    type,
  } = props;
  const titleLinkVal = titleLink || link;
  const imageContainerClasses = [Style.imageContainer, Style.maintainAspectRatio];
  if (scaleFromLeft) imageContainerClasses.push(Style.scaleFromLeft);
  if (scaleFromRight) imageContainerClasses.push(Style.scaleFromRight);

  const onSubtitleOnClick = useCallback(() => {
    if (onTileClick) {
      onTileClick({
        account,
        name,
        originalItem,
        percentWatched,
        recommendationIdData,
        subtitleText,
        titleText,
        uiElement: SegmentUiElements.SERIES_EPISODE,
      });
    }
  }, [
    onTileClick,
    originalItem,
    name,
    percentWatched,
    account,
    subtitleText,
    titleText,
    recommendationIdData,
  ]);

  const onTileTitleClick = useCallback(() => {
    setSessionStorageItem({ key: LAST_SESSION_PATH, value: window.location.pathname });
    onTileClick({ uiElement: SegmentUiElements.SERIES_EPISODE });
    if (onTitleClick) {
      onTitleClick({
        account,
        gridIndex,
        gridTitle,
        itemIndex,
        name,
        network,
        originalItem,
        percentWatched,
        recommendationIdData,
        subtitleText,
        title,
        titleText,
        uiElement: SegmentUiElements.SERIES_TITLE,
      });
    }
  }, [
    account,
    gridIndex,
    gridTitle,
    itemIndex,
    name,
    network,
    onTileClick,
    onTitleClick,
    originalItem,
    percentWatched,
    recommendationIdData,
    subtitleText,
    title,
    titleText,
  ]);

  const titleOptions = {
    ...titleProcessor(props),
    link: titleLinkVal,
    onClick: onTileTitleClick,
    Style,
  };
  const isMobile = isMobileDevice(breakpoint);
  const isGenre = collectionType === COLLECTION_TYPE_GENRE;
  const isLiveCollection = collectionType === COLLECTION_TYPE_LIVE;
  const isLiveNow = originalItem?.isLiveNow;
  const shouldRenderPlayButton =
    !isMobile && !isGenre && (!isLiveCollection || (isLiveCollection && isLiveNow));

  const content = (
    <>
      <TileImage image={image || hoverImage} altText={altText} theme={Style} />
      <Badge theme={Style} badge={badge} durationInSeconds={durationInSeconds} isHoverStateV2 />
      <Overlay theme={Style} render={!isMobile}>
        <PlayButton
          link={isSeries ? titleLinkVal : link}
          playButtonClick={playButtonClickProcessor({ type, props })}
          render={shouldRenderPlayButton}
          theme={Style}
        />
      </Overlay>
      <MobilePlayButton
        render={isMobile}
        theme={Style}
        link={isSeries ? titleLinkVal : link}
        playButtonClick={playButtonClickProcessor({ type, props })}
      />
    </>
  );

  return (
    <div className={theme.tile}>
      {isInternalLink(link || titleLink) ? (
        <Link
          to={link || titleLink}
          className={classNames(imageContainerClasses)}
          onClick={() => (isMobile ? playButtonClickProcessor({ type, props }) : noop)}
        >
          {content}
        </Link>
      ) : (
        <a
          href={link || titleLink}
          className={classNames(imageContainerClasses)}
          onClick={() => (isMobile ? playButtonClickProcessor({ type, props }) : noop)}
        >
          {content}
        </a>
      )}
      <Detail theme={Style}>
        <div className={Style.info}>
          <TileTitle {...titleOptions} />
          <Subtitle
            isDisabled={false}
            isRedirect={isRedirect}
            link={link}
            onClick={onSubtitleOnClick}
            subtitle={subtitle}
            subtitleLines={subtitleLines}
            target={target}
            theme={Style}
            truncateSubtitle={truncateSubtitle}
          />
          <Metadata theme={Style} metadata={metadata} />
        </div>
      </Detail>
    </div>
  );
};

export default ClipTile;

ClipTile.propTypes = {
  account: PropTypes.shape({}),
  altText: PropTypes.string,
  badge: PropTypes.string,
  breakpoint: PropTypes.string.isRequired,
  callSign: PropTypes.string,
  collectionType: PropTypes.string,
  disableHoverStateActionButtons: PropTypes.bool,
  durationInSeconds: PropTypes.number,
  endDate: PropTypes.string,
  favoritesManager: PropTypes.shape({}),
  gridIndex: PropTypes.number,
  gridTitle: PropTypes.string,
  hoverImage: PropTypes.string,
  image: PropTypes.string,
  isAnonymous: PropTypes.bool,
  isRedirect: PropTypes.bool,
  isSeries: PropTypes.bool,
  isSeriesDetailAvailable: PropTypes.bool,
  itemIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  link: PropTypes.string,
  metadata: PropTypes.string,
  name: PropTypes.string,
  network: PropTypes.string,
  onTileClick: PropTypes.func,
  onTitleClick: PropTypes.func,
  originalItem: PropTypes.shape({}),
  overlayProps: PropTypes.shape({}),
  percentWatched: PropTypes.number,
  recommendationIdData: PropTypes.oneOfType([PropTypes.node, PropTypes.shape({})]),
  scaleFromLeft: PropTypes.bool,
  scaleFromRight: PropTypes.bool,
  segmentPlaylistButtonAction: PropTypes.func,
  seriesName: PropTypes.string,
  seriesScreenUrl: PropTypes.string,
  showCode: PropTypes.string,
  showLiveProgressBar: PropTypes.bool,
  startDate: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleLines: PropTypes.number,
  subtitleText: PropTypes.string,
  target: PropTypes.string,
  theme: PropTypes.shape({}).isRequired,
  title: PropTypes.string,
  titleLink: PropTypes.string,
  titleText: PropTypes.string,
  truncateSubtitle: PropTypes.bool,
  type: PropTypes.string,
  videoType: PropTypes.string,
};

ClipTile.defaultProps = {
  account: {},
  altText: null,
  badge: null,
  callSign: null,
  collectionType: null,
  disableHoverStateActionButtons: false,
  durationInSeconds: 0,
  endDate: null,
  favoritesManager: {},
  gridIndex: null,
  gridTitle: null,
  hoverImage: null,
  image: null,
  isAnonymous: false,
  isRedirect: false,
  isSeries: false,
  isSeriesDetailAvailable: false,
  itemIndex: null,
  link: null,
  metadata: null,
  name: null,
  network: null,
  onTileClick: noop,
  onTitleClick: noop,
  originalItem: null,
  overlayProps: null,
  percentWatched: null,
  recommendationIdData: null,
  scaleFromLeft: false,
  scaleFromRight: false,
  segmentPlaylistButtonAction: noop,
  seriesName: null,
  seriesScreenUrl: null,
  showCode: null,
  showLiveProgressBar: false,
  startDate: null,
  subtitle: null,
  subtitleLines: null,
  subtitleText: null,
  target: null,
  title: null,
  titleLink: null,
  titleText: null,
  truncateSubtitle: false,
  type: null,
  videoType: null,
};
